/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 *
 * Alert Subscription: who is subscribed to an alert, through which channels, and over which assets.
 *
 * The stored entity speaks in raw identifiers — `user:sql46367`, `entity:asset:sql291901`, nested
 * `alertNotificationMedia.raise.email` booleans, and optional included / excluded asset groups.
 * None of that reads well as a flat diff, so this renderer presents it the way the subscription
 * screen does: a headline naming who subscribed or unsubscribed and from which alert, then a
 * raise/clear/repeat × email/sms notification matrix and the two asset groups with individual
 * assets resolved to names.
 *
 * This renderer opts into `handlesCreateDelete`. A create embeds the whole entity in one diff entry;
 * a delete instead writes one removal per top-level field, and both are reassembled into the same
 * snapshot. Field-level updates are read from the diff.
 */

import React from 'react'

import { FiUserMinus, FiUserPlus } from 'react-icons/fi'

import type { Group, GroupDelta, GroupMember } from './entityGroups'
import {
  asRecord,
  buildGroupDelta,
  deltaCount,
  deltaMembers,
  groupTypeLabel,
  individualMembers,
  stripSqlId,
} from './entityGroups'
import { SmartGroupName } from './SmartGroupName'
import type { RendererProps } from './types'
import { useAssetNames, useUserName } from './useAuditLookups'

import type { AuditDetail } from '../../types'
import type { NormalizedChange } from '../diff/normalize'
import { ChangeList, LookupLoading, Section } from '../primitives'

// ---- Snapshot shape --------------------------------------------------------

interface Subscription {
  recipientUserId?: string
  includedEntityGroup?: Group
  excludedEntityGroup?: Group
  alertNotificationMedia?: Record<string, Record<string, unknown>>
}

/**
 * Storage plumbing the subscription screen never shows and support never asks about. Listing the
 * CouchDB revision and the document type as "changes" buries the two facts that matter — who
 * unsubscribed, and from what — under rows nobody reads.
 */
const NOISE_FIELDS = new Set(['_id', '_rev', 'type', 'alertConfigId'])

/** Fields this renderer presents in a section of their own; anything else is genuinely unexpected. */
const PRESENTED_FIELDS = new Set([
  'recipientUserId',
  'includedEntityGroup',
  'excludedEntityGroup',
  'alertNotificationMedia',
])

/**
 * The whole entity, however the writer chose to record it.
 *
 * A create embeds the entity under a single empty-path entry. A delete (an unsubscribe) instead
 * writes one remove entry per top-level field, which would otherwise fall through to the field-level
 * view and read as seven unrelated deletions. Both describe one entity, so both are reassembled into
 * one snapshot here.
 */
const snapshotOf = (changes: NormalizedChange[]): Subscription | null => {
  const whole = changes.find((c) => c.segments.length === 0)
  if (whole) return asRecord(whole.after ?? whole.before) as Subscription | null

  // Every field removed at once, with nothing left behind, is the entity itself.
  const fields = changes.filter((c) => c.segments.length === 1)
  if (fields.length === 0 || fields.length !== changes.length) return null
  if (!fields.every((c) => c.state === 'REMOVED')) return null

  const entity: Record<string, unknown> = {}
  for (const change of fields) entity[change.segments[0]] = change.before ?? change.after
  return entity as Subscription
}

/** Top-level fields carrying something we do not already present, and are not storage plumbing. */
const unexpectedFields = (changes: NormalizedChange[]): NormalizedChange[] =>
  changes.filter(
    (c) => c.segments.length === 1 && !NOISE_FIELDS.has(c.segments[0]) && !PRESENTED_FIELDS.has(c.segments[0])
  )

// ---- Notification matrix ---------------------------------------------------

/**
 * The subscription screen offers three rows of toggles — Raise, Clear and Repeat — each with an
 * email and an SMS channel. The stored entity only carries the keys that were actually set, and
 * `reset` is the model's name for what the screen calls Clear.
 */
const ACTION_LABELS: Record<string, string> = {
  raise: 'Raise',
  reset: 'Clear',
  repeat: 'Repeat',
}

/** Row order, matching the subscription screen. Rows are shown even when the entity omits them. */
const CANONICAL_ACTIONS = ['raise', 'reset', 'repeat']

const humaniseAction = (key: string): string =>
  ACTION_LABELS[key] ?? key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1')

interface Action {
  key: string
  label: string
}

/**
 * The canonical rows first, then any action key the data carries that we don't know about — a new
 * notification trigger must still show up rather than being silently dropped.
 */
const actionsFor = (presentKeys: readonly string[]): Action[] => {
  const extras = presentKeys.filter((key) => !CANONICAL_ACTIONS.includes(key))
  const keys = [...CANONICAL_ACTIONS, ...Array.from(new Set(extras)).sort()]
  return keys.map((key) => ({ key, label: humaniseAction(key) }))
}

const CHANNEL_LABELS: Record<string, string> = {
  email: 'Email',
  sms: 'SMS',
  push: 'Push',
}

const CANONICAL_CHANNELS = ['email', 'sms']

/** Columns, on the same rule as the rows: the two the screen offers, then anything else present. */
const channelsFor = (presentKeys: readonly string[]): Action[] => {
  const extras = presentKeys.filter((key) => !CANONICAL_CHANNELS.includes(key))
  const keys = [...CANONICAL_CHANNELS, ...Array.from(new Set(extras)).sort()]
  return keys.map((key) => ({ key, label: CHANNEL_LABELS[key] ?? humaniseAction(key) }))
}

const Dot: React.FC<{ on?: boolean }> = ({ on }) => (
  <span aria-hidden className={on ? 'text-success' : 'text-base-content/30'}>
    {on ? '●' : '○'}
  </span>
)

interface Cell {
  before?: boolean
  after?: boolean
  changed: boolean
}

const MatrixCell: React.FC<{ cell?: Cell }> = ({ cell }) => {
  if (!cell) return <td className="px-2 py-1.5 text-center text-base-content/20">–</td>

  if (!cell.changed) {
    const on = cell.after ?? cell.before
    return (
      <td className="px-2 py-1.5 text-center">
        <Dot on={on} />
        <span className="sr-only">{on ? 'On' : 'Off'}</span>
      </td>
    )
  }

  return (
    <td className="px-2 py-1.5 text-center">
      <span className="inline-flex items-center gap-1 rounded-field bg-accent/10 px-1.5 py-0.5 ring-1 ring-accent">
        <Dot on={cell.before} />
        <span aria-hidden className="text-[10px] text-base-content/60">
          →
        </span>
        <Dot on={cell.after} />
      </span>
      <span className="sr-only">
        changed from {cell.before ? 'on' : 'off'} to {cell.after ? 'on' : 'off'}
      </span>
    </td>
  )
}

const NotificationMatrix: React.FC<{
  actions: Action[]
  channels: Action[]
  cellFor: (action: string, channel: string) => Cell | undefined
}> = ({ actions, channels, cellFor }) => (
  <div className="flex flex-col gap-2">
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-[11px] uppercase tracking-wide text-base-content/60">
            <th scope="col" className="px-2 py-1 text-left font-semibold">
              When
            </th>
            {channels.map((channel) => (
              <th key={channel.key} scope="col" className="px-2 py-1 text-center font-semibold">
                {channel.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-base-200">
          {actions.map((action) => (
            <tr key={action.key}>
              <th scope="row" className="px-2 py-1.5 text-left text-sm font-medium">
                {action.label}
              </th>
              {channels.map((channel) => (
                <MatrixCell key={channel.key} cell={cellFor(action.key, channel.key)} />
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    <p className="flex flex-wrap items-center gap-3 text-xs text-base-content/60">
      <span className="flex items-center gap-1">
        <Dot on /> on
      </span>
      <span className="flex items-center gap-1">
        <Dot /> off
      </span>
      <span className="flex items-center gap-1">
        <span aria-hidden className="text-base-content/20">
          –
        </span>{' '}
        not recorded
      </span>
    </p>
  </div>
)

// ---- Asset group section ---------------------------------------------------

type ChipTone = 'neutral' | 'added' | 'removed'

const CHIP_TONES: Record<ChipTone, string> = {
  neutral: 'border-base-300 bg-base-200',
  added: 'border-success/40 bg-success/10 text-success',
  removed: 'border-error/40 bg-error/10 text-error line-through',
}

const AssetChip: React.FC<{ id: string; name?: string; tone?: ChipTone }> = ({ id, name, tone = 'neutral' }) => (
  <span className={`inline-flex items-center gap-1 rounded-selector border px-2 py-0.5 text-xs ${CHIP_TONES[tone]}`}>
    {tone !== 'neutral' && <span className="sr-only">{tone === 'added' ? 'Added:' : 'Removed:'}</span>}
    <span className="font-medium" title={`Asset #${id}`}>
      {name ?? `Asset #${id}`}
    </span>
  </span>
)

const AssetGroupBody: React.FC<{ group: Group; assetNames: Map<string, string>; companyId: number }> = ({
  group,
  assetNames,
  companyId,
}) => {
  if (!group) return <p className="text-sm text-base-content/60">None.</p>

  if (group.groupType === 'IndividualAssets') {
    const members = individualMembers(group)
    if (members.length === 0) return <p className="text-sm text-base-content/60">No assets.</p>
    return (
      <div className="flex flex-wrap gap-1.5">
        {members.map((member) => (
          <AssetChip key={member.id} id={member.id} name={assetNames.get(member.id)} />
        ))}
      </div>
    )
  }

  // A saved group (SmartGroup and anything else) — there is no individual asset list to expand.
  return (
    <p className="text-sm">
      <span className="font-medium">{groupTypeLabel(group.groupType)}</span>
      {group.id != null && (
        <span className="ml-1 text-base-content/70">
          {group.groupType === 'SmartGroup' ? (
            <SmartGroupName id={group.id} company={group.companyId} fallbackCompanyId={companyId} />
          ) : (
            `#${stripSqlId(group.id)}`
          )}
        </span>
      )}
    </p>
  )
}

// ---- Asset group changes ---------------------------------------------------

/** One side of a membership change: a labelled row of chips, or nothing at all. */
const ChipRow: React.FC<{
  label: string
  members: GroupMember[]
  tone: ChipTone
  assetNames: Map<string, string>
}> = ({ label, members, tone, assetNames }) => {
  if (members.length === 0) return null
  return (
    <div className="flex flex-col gap-1.5">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-base-content/60">{label}</p>
      <div className="flex flex-wrap gap-1.5">
        {members.map((member) => (
          <AssetChip key={member.id} id={member.id} name={assetNames.get(member.id)} tone={tone} />
        ))}
      </div>
    </div>
  )
}

const GroupDeltaBody: React.FC<{ delta: GroupDelta; assetNames: Map<string, string>; companyId: number }> = ({
  delta,
  assetNames,
  companyId,
}) => {
  const selectionChanged = delta.typeAfter !== undefined || delta.typeBefore !== undefined
  const savedGroupChanged = delta.idAfter !== undefined || delta.idBefore !== undefined
  const nothingToShow =
    !selectionChanged && !savedGroupChanged && delta.added.length === 0 && delta.removed.length === 0

  return (
    <div className="flex flex-col gap-3">
      {selectionChanged && (
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-base-content/60">Selection</span>
          <span className="text-error line-through">{groupTypeLabel(delta.typeBefore)}</span>
          <span aria-hidden className="text-base-content/50">
            →
          </span>
          <span className="font-medium text-success">{groupTypeLabel(delta.typeAfter)}</span>
        </div>
      )}

      {savedGroupChanged && (
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-base-content/60">Smart group</span>
          {delta.idBefore !== undefined && (
            <span className="text-error line-through">
              <SmartGroupName id={delta.idBefore} company={delta.companyBefore} fallbackCompanyId={companyId} />
            </span>
          )}
          {delta.idBefore !== undefined && delta.idAfter !== undefined && (
            <span aria-hidden className="text-base-content/50">
              →
            </span>
          )}
          {delta.idAfter !== undefined && (
            <span className="font-medium text-success">
              <SmartGroupName id={delta.idAfter} company={delta.companyAfter} fallbackCompanyId={companyId} />
            </span>
          )}
        </div>
      )}

      <ChipRow label="Added" members={delta.added} tone="added" assetNames={assetNames} />
      <ChipRow label="Removed" members={delta.removed} tone="removed" assetNames={assetNames} />

      {nothingToShow && <p className="text-sm text-base-content/60">The selection changed.</p>}

      {delta.other.length > 0 && <ChangeList changes={delta.other} />}
    </div>
  )
}

// ---- Headline --------------------------------------------------------------

/**
 * What actually happened, in one sentence, before any of the detail.
 *
 * A subscription create or delete is a person subscribing or unsubscribing from a named alert. That
 * is the fact a support engineer opens the record for; the notification matrix and the asset lists
 * are context for it, not the answer. Stating it plainly at the top means the record no longer has
 * to be reconstructed from a list of removed fields.
 */
const Headline: React.FC<{
  unsubscribed: boolean
  alertName: string
  freshUserId: string
  userName?: string
}> = ({ unsubscribed, alertName, freshUserId, userName }) => {
  const Icon = unsubscribed ? FiUserMinus : FiUserPlus
  const tone = unsubscribed ? 'border-error/40 bg-error/10 text-error' : 'border-success/40 bg-success/10 text-success'
  const who = userName ?? (freshUserId ? `User #${freshUserId}` : 'A user')

  return (
    <div className={`flex items-start gap-3 rounded-box border p-3 ${tone}`}>
      <Icon aria-hidden className="mt-0.5 h-5 w-5 shrink-0" />
      <div className="min-w-0">
        <p className="text-base font-semibold text-base-content">
          {who} {unsubscribed ? 'unsubscribed from' : 'subscribed to'}{' '}
          {alertName ? <span className="font-semibold">{alertName}</span> : 'this alert'}
        </p>
        <p className="mt-0.5 text-xs text-base-content/70">
          {unsubscribed
            ? 'These were the subscription’s settings when it was removed.'
            : 'The subscription was created with these settings.'}
        </p>
      </div>
    </div>
  )
}

// ---- Snapshot (create / delete) --------------------------------------------

const SnapshotView: React.FC<{
  audit: AuditDetail
  subscription: Subscription
  extras: NormalizedChange[]
}> = ({ audit, subscription, extras }) => {
  const freshUserId = subscription.recipientUserId ? stripSqlId(subscription.recipientUserId) : ''
  const media = subscription.alertNotificationMedia ?? {}

  const assetIds = React.useMemo(
    () =>
      [
        ...individualMembers(subscription.includedEntityGroup ?? null),
        ...individualMembers(subscription.excludedEntityGroup ?? null),
      ].map((member) => member.id),
    [subscription.includedEntityGroup, subscription.excludedEntityGroup]
  )

  const { data: userName, isLoading: userLoading } = useUserName(freshUserId || undefined)
  const { data: assetNames, isLoading: assetsLoading } = useAssetNames([audit.companyId], assetIds)
  const names = assetNames ?? new Map<string, string>()

  const actions = actionsFor(Object.keys(media))
  const channels = channelsFor(Object.values(media).flatMap((row) => Object.keys(asRecord(row) ?? {})))

  const cellFor = (action: string, channel: string): Cell | undefined => {
    const value = media[action]?.[channel]
    if (typeof value !== 'boolean') return undefined
    return { after: value, changed: false }
  }

  const included = subscription.includedEntityGroup ?? null
  const excluded = subscription.excludedEntityGroup ?? null

  // Every value here is new on a create and gone on a delete. Tinting the section headers says so
  // in the same colours the panel legend already explains, instead of reading as a neutral update.
  const unsubscribed = audit.changeType === 'delete'
  const tone = unsubscribed ? 'error' : 'success'
  const suffix = unsubscribed ? ' — removed' : ''

  if (userLoading || assetsLoading) return <LookupLoading />

  return (
    <div className="flex flex-col gap-3">
      <Headline
        unsubscribed={unsubscribed}
        alertName={audit.entityName}
        freshUserId={freshUserId}
        userName={userName}
      />

      <Section title={`Notifications${suffix}`} tone={tone}>
        <NotificationMatrix actions={actions} channels={channels} cellFor={cellFor} />
      </Section>

      {included && (
        <Section title={`Included assets${suffix}`} tone={tone}>
          <AssetGroupBody group={included} assetNames={names} companyId={audit.companyId} />
        </Section>
      )}

      {excluded && (
        <Section title={`Excluded assets${suffix}`} tone={tone}>
          <AssetGroupBody group={excluded} assetNames={names} companyId={audit.companyId} />
        </Section>
      )}

      {/* Storage plumbing is dropped, but a field we do not recognise must not vanish with it. */}
      {extras.length > 0 && (
        <Section title="Other values" count={extras.length} defaultOpen={false} tone={tone}>
          <ChangeList changes={extras} />
        </Section>
      )}
    </div>
  )
}

// ---- Field-level update ----------------------------------------------------

/**
 * Any change under `alertNotificationMedia`, at whichever depth the writer recorded it.
 *
 * A single toggle arrives as `alertNotificationMedia.raise.sms` with boolean values, but turning on
 * a trigger that had never been set arrives as `alertNotificationMedia.repeat` carrying the whole
 * `{ email, sms }` object — and the whole media object can be replaced at once. All three describe
 * the same matrix, so all three belong in it rather than in a generic "other changes" list.
 */
const isMediaChange = (c: NormalizedChange): boolean => {
  if (c.segments[0] !== 'alertNotificationMedia') return false
  if (c.segments.length === 3) return typeof c.before === 'boolean' || typeof c.after === 'boolean'
  if (c.segments.length === 1 || c.segments.length === 2) return Boolean(asRecord(c.before) ?? asRecord(c.after))
  return false
}

/**
 * Flatten one media change into `action|channel` cells.
 *
 * The two object-shaped forms are expanded a level (or two) so that a change recorded as an object
 * still reads as the same before/after toggles a per-field change would have produced. A channel
 * absent from one side is left `undefined`, which the cell renders as off — the value it had.
 */
const cellsFromChange = (change: NormalizedChange): [string, Cell][] => {
  const [, action, channel] = change.segments

  if (change.segments.length === 3) {
    return [
      [
        `${action}|${channel}`,
        {
          before: typeof change.before === 'boolean' ? change.before : undefined,
          after: typeof change.after === 'boolean' ? change.after : undefined,
          changed: true,
        },
      ],
    ]
  }

  // `alertNotificationMedia.<action>` — one row's worth of channels.
  if (change.segments.length === 2) {
    const before = asRecord(change.before) ?? {}
    const after = asRecord(change.after) ?? {}
    return Array.from(new Set([...Object.keys(before), ...Object.keys(after)])).map((key) => [
      `${action}|${key}`,
      {
        before: typeof before[key] === 'boolean' ? (before[key] as boolean) : undefined,
        after: typeof after[key] === 'boolean' ? (after[key] as boolean) : undefined,
        changed: true,
      },
    ])
  }

  // `alertNotificationMedia` — the whole matrix at once.
  const before = asRecord(change.before) ?? {}
  const after = asRecord(change.after) ?? {}
  return Array.from(new Set([...Object.keys(before), ...Object.keys(after)])).flatMap((actionKey) => {
    const beforeRow = asRecord(before[actionKey]) ?? {}
    const afterRow = asRecord(after[actionKey]) ?? {}
    return Array.from(new Set([...Object.keys(beforeRow), ...Object.keys(afterRow)])).map((key): [string, Cell] => [
      `${actionKey}|${key}`,
      {
        before: typeof beforeRow[key] === 'boolean' ? (beforeRow[key] as boolean) : undefined,
        after: typeof afterRow[key] === 'boolean' ? (afterRow[key] as boolean) : undefined,
        changed: true,
      },
    ])
  })
}

/** The two asset groups, in the order the subscription screen lists them. */
const GROUPS = [
  { root: 'includedEntityGroup', title: 'Included assets' },
  { root: 'excludedEntityGroup', title: 'Excluded assets' },
] as const

const UpdateView: React.FC<{ audit: AuditDetail; changes: NormalizedChange[] }> = ({ audit, changes }) => {
  const mediaChanges = changes.filter(isMediaChange)
  const groupChanges = changes.filter(
    (c) => c.segments[0] === 'includedEntityGroup' || c.segments[0] === 'excludedEntityGroup'
  )
  const claimed = new Set<NormalizedChange>([...mediaChanges, ...groupChanges])
  const remaining = changes.filter((c) => !claimed.has(c))

  // One delta per group, so an edit touching both is read as two membership changes rather than as
  // an interleaved list of indexed paths.
  const deltas = GROUPS.map((group) => ({
    ...group,
    delta: buildGroupDelta(groupChanges.filter((c) => c.segments[0] === group.root)),
    present: groupChanges.some((c) => c.segments[0] === group.root),
  }))

  // The hook keys its query on the joined ids, so a fresh array each render costs nothing.
  const { data: assetNames, isLoading: assetsLoading } = useAssetNames(
    [audit.companyId],
    deltas.flatMap((entry) => deltaMembers(entry.delta).map((member) => member.id))
  )
  const names = assetNames ?? new Map<string, string>()

  const cells = new Map<string, Cell>(mediaChanges.flatMap(cellsFromChange))
  const cellFor = (action: string, channel: string) => cells.get(`${action}|${channel}`)

  // Rows come from the cells rather than the change paths, so a whole-object change still names
  // every action it touched.
  const actions = actionsFor(Array.from(cells.keys(), (key) => key.split('|')[0]))
  const channels = channelsFor(Array.from(cells.keys(), (key) => key.split('|')[1]))
  // A change touching several toggles is one diff entry but several edits; count what changed.
  const toggleCount = cells.size

  if (assetsLoading) return <LookupLoading />

  return (
    <div className="flex flex-col gap-3">
      {mediaChanges.length > 0 && (
        <Section title="Notifications" count={toggleCount}>
          <NotificationMatrix actions={actions} channels={channels} cellFor={cellFor} />
        </Section>
      )}

      {deltas
        .filter((entry) => entry.present)
        .map((entry) => (
          <Section key={entry.root} title={entry.title} count={deltaCount(entry.delta)}>
            <GroupDeltaBody delta={entry.delta} assetNames={names} companyId={audit.companyId} />
          </Section>
        ))}

      {remaining.length > 0 && (
        <Section title="Other changes" count={remaining.length}>
          <ChangeList changes={remaining} />
        </Section>
      )}
    </div>
  )
}

// ---- Entry point -----------------------------------------------------------

export const AlertSubscriptionRenderer: React.FC<RendererProps> = ({ audit, changes }) => {
  const subscription = snapshotOf(changes)
  if (subscription) return <SnapshotView audit={audit} subscription={subscription} extras={unexpectedFields(changes)} />
  return <UpdateView audit={audit} changes={changes} />
}
